import { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Paragraph from '@tiptap/extension-paragraph';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { TextAlign } from '@tiptap/extension-text-align';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { extractImageFilesFromClipboardItems, pasteNormalizer } from '../../../services/pasteNormalizer';
import styles from './RichTextEditor.module.css';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];
const COLOURS = [
  '#000000',
  '#374151',
  '#6B7280',
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#ffffff',
  '#d1d5db',
  '#9ca3af',
  '#fca5a5',
  '#fed7aa',
];

function preserveHtmlAttribute(attribute: string) {
  return {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute(attribute),
    renderHTML: (attributes: Record<string, string | null>) =>
      attributes[attribute] ? { [attribute]: attributes[attribute] } : {},
  };
}

const preservedTableAttributes = {
  style: preserveHtmlAttribute('style'),
  width: preserveHtmlAttribute('width'),
  align: preserveHtmlAttribute('align'),
  valign: preserveHtmlAttribute('valign'),
  cellpadding: preserveHtmlAttribute('cellpadding'),
  cellspacing: preserveHtmlAttribute('cellspacing'),
  border: preserveHtmlAttribute('border'),
};

const preservedCellAttributes = {
  style: preserveHtmlAttribute('style'),
  width: preserveHtmlAttribute('width'),
  align: preserveHtmlAttribute('align'),
  valign: preserveHtmlAttribute('valign'),
};

const SignatureParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      style: preserveHtmlAttribute('style'),
      align: preserveHtmlAttribute('align'),
    };
  },
});

const SignatureImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      style: preserveHtmlAttribute('style'),
      width: preserveHtmlAttribute('width'),
      height: preserveHtmlAttribute('height'),
      align: preserveHtmlAttribute('align'),
    };
  },
});

const SignatureLink = Link.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      style: preserveHtmlAttribute('style'),
      title: preserveHtmlAttribute('title'),
      target: preserveHtmlAttribute('target'),
      rel: preserveHtmlAttribute('rel'),
    };
  },
});

const SignatureTable = Table.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      ...preservedTableAttributes,
    };
  },
});

const SignatureTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      style: preserveHtmlAttribute('style'),
      align: preserveHtmlAttribute('align'),
      valign: preserveHtmlAttribute('valign'),
    };
  },
});

const SignatureTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      ...preservedCellAttributes,
    };
  },
});

const SignatureTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      ...preservedCellAttributes,
    };
  },
});

export default function RichTextEditor({ content, onChange, placeholder }: RichTextEditorProps) {
  // Memoize extensions to prevent re-creation on every render
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        paragraph: false,
        link: false,
        underline: false,
      }),
      SignatureParagraph,
      Underline.configure({
        HTMLAttributes: {
          style: null,
        },
      }),
      TextStyle.configure({
        HTMLAttributes: {
          style: null,
        },
      }),
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      SignatureImage.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          style: null,
        },
      }),
      SignatureLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: null,
        },
      }),
      SignatureTable.configure({
        resizable: true,
        HTMLAttributes: {
          style: null,
        },
      }),
      SignatureTableRow.configure({
        HTMLAttributes: {
          style: null,
        },
      }),
      SignatureTableHeader.configure({
        HTMLAttributes: {
          style: null,
        },
      }),
      SignatureTableCell.configure({
        HTMLAttributes: {
          style: null,
        },
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: styles.editorArea,
        'data-placeholder': placeholder ?? 'Write your email signature here...',
      },
      // Preserve inline styles when pasting HTML
      transformPastedHTML(html) {
        return html; // Don't strip any HTML/styles from pasted content
      },
      handlePaste(_view, event) {
        const html = event.clipboardData?.getData('text/html') || '';
        const text = event.clipboardData?.getData('text/plain') || '';
        const imageFiles = extractImageFilesFromClipboardItems(event.clipboardData?.items ?? null);

        // Let ProseMirror handle empty clipboard payloads.
        if (!html.trim() && !text.trim() && imageFiles.length === 0) {
          return false;
        }

        event.preventDefault();

        void (async () => {
          try {
            const normalized = await pasteNormalizer.normalize({
              html,
              text,
              imageFiles,
              clipboardItems: event.clipboardData?.items ?? null,
            });

            if (normalized.kind === 'none') {
              return;
            }

            editor
              ?.chain()
              .insertContent(normalized.content, { parseOptions: { preserveWhitespace: 'full' } })
              .run();
          } catch {
            if (text.trim()) {
              editor
                ?.chain()
                .insertContent(text, { parseOptions: { preserveWhitespace: 'full' } })
                .run();
            }
          }
        })();

        return true;
      },
    },
  });

  // Keep editor in sync when 'content' prop changes (e.g. after API load)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  if (!editor) return null;

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (re) => {
        const src = re.target?.result as string;
        if (src) editor.chain().focus().setImage({ src }).run();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href || 'https://';
    const url = window.prompt('Enter URL:', previousUrl);
    if (!url) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const applyExactColour = () => {
    const colour = window.prompt('Enter a hex colour (for example #1F2937):', '#000000');
    if (!colour) return;
    editor.chain().focus().setColor(colour).run();
  };

  return (
    <div className={styles.wrapper}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        {/* Text style */}
        <div className={styles.toolGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? styles.active : ''}
            title="Bold"
          >
            <b>B</b>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? styles.active : ''}
            title="Italic"
          >
            <i>I</i>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? styles.active : ''}
            title="Underline"
          >
            <u>U</u>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive('strike') ? styles.active : ''}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
        </div>

        <div className={styles.divider} />

        {/* Font size */}
        <div className={styles.toolGroup}>
          <select
            title="Font size"
            onChange={(e) =>
              editor.chain().focus().setMark('textStyle', { fontSize: e.target.value }).run()
            }
            defaultValue=""
          >
            <option value="" disabled>
              Size
            </option>
            {FONT_SIZES.map((s) => (
              <option key={s} value={s}>
                {s.replace('px', 'pt')}
              </option>
            ))}
          </select>
        </div>

        {/* Colour */}
        <div className={styles.toolGroup}>
          <div className={styles.colourPicker} title="Text colour">
            <span>A</span>
            <div className={styles.colourDropdown}>
              {COLOURS.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  style={{ background: c, border: '1px solid #d1d5db' }}
                  onClick={() => editor.chain().focus().setColor(c).run()}
                />
              ))}
            </div>
          </div>
          <input
            type="color"
            title="Choose exact colour"
            aria-label="Choose exact colour"
            className={styles.colourInput}
            defaultValue="#000000"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
          <button type="button" onClick={applyExactColour} title="Enter exact hex colour">
            #
          </button>
        </div>

        <div className={styles.divider} />

        {/* Alignment */}
        <div className={styles.toolGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? styles.active : ''}
            title="Align left"
          >
            ≡
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? styles.active : ''}
            title="Centre"
          >
            ☰
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? styles.active : ''}
            title="Align right"
          >
            ≡
          </button>
        </div>

        <div className={styles.divider} />

        {/* Lists */}
        <div className={styles.toolGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? styles.active : ''}
            title="Bullet list"
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? styles.active : ''}
            title="Numbered list"
          >
            1. List
          </button>
        </div>

        <div className={styles.divider} />

        {/* Insert */}
        <div className={styles.toolGroup}>
          <button
            type="button"
            onClick={addLink}
            className={editor.isActive('link') ? styles.active : ''}
            title="Insert link"
          >
            🔗 Link
          </button>
          <button type="button" onClick={removeLink} title="Remove link">
            ⛓ Remove
          </button>
          <button type="button" onClick={addImage} title="Insert image">
            🖼 Image
          </button>
          <button type="button" onClick={insertTable} title="Insert table">
            ⊞ Table
          </button>
          <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row">
            + Row
          </button>
          <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column">
            + Col
          </button>
          <button type="button" onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row">
            - Row
          </button>
          <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column">
            - Col
          </button>
          <button type="button" onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table">
            ⌫ Table
          </button>
        </div>

        <div className={styles.divider} />

        {/* Undo/Redo */}
        <div className={styles.toolGroup}>
          <button type="button" onClick={() => editor.chain().focus().undo().run()} title="Undo">
            ↩
          </button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()} title="Redo">
            ↪
          </button>
        </div>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
